import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  DataTablePagination,
} from "@/components/ui/data-table";
import {
  AffiliateFilterBar,
  AgeFilter,
  RevenueFilter,
  AGE_MAX,
} from "../../../components/common/AffiliateFilterBar";
import {
  programService,
  ProgramApplication,
  GetApplicationsParams,
} from "../../../services/program.service";
import { Users, Facebook, Instagram, Youtube } from "react-feather";
import TikTokIcon from "../../../assets/tiktok";
import { toast } from "react-hot-toast";
import { nicheService } from "../../../services/niche.service";
import debounce from "lodash/debounce";
import PageHeader from "../../../components/common/PageHeader";
import { EmptyState } from "@/components/ui/empty-state";
import { TableCard } from "@/components/ui/table-card";
import { AxiosError } from "axios";

interface FilterState {
  country: string[];
  niche: string[];
  gender: "MALE" | "FEMALE" | "";
  age: AgeFilter;
  revenue: RevenueFilter;
}

const DEFAULT_FILTERS: FilterState = {
  country: [],
  niche: [],
  gender: "",
  age: { range: null, includeNoAge: true },
  revenue: { min: null, max: null },
};

export function Applicants() {
  const navigate = useNavigate();
  const [selectedApplicants, setSelectedApplicants] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applications, setApplications] = useState<ProgramApplication[]>([]);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [niches, setNiches] = useState<Array<{ id: string; name: string }>>([]);
  const [isProcessing, setIsProcessing] = useState<{
    approve: boolean;
    reject: boolean;
  }>({ approve: false, reject: false });

  // Fetch niches on component mount
  useEffect(() => {
    const fetchNiches = async () => {
      try {
        const nicheData = await nicheService.getAvailableNiches();
        setNiches(nicheData);
      } catch (err) {
        console.error("Error fetching niches:", err);
      }
    };
    fetchNiches();
  }, []);

  const fetchApplications = useCallback(
    async (search: string, currentFilters: FilterState) => {
      setIsLoading(true);
      setError(null);
      try {
        const params: GetApplicationsParams = {
          page: currentPage,
          limit: pageSize,
        };

        if (search) {
          params.keyword = search;
          params.searchBy = "NAME";
        }

        if (currentFilters.country.length > 0)
          params.countries = currentFilters.country.join(",");
        if (currentFilters.gender) params.gender = currentFilters.gender;
        if (currentFilters.niche.length > 0)
          params.niches = currentFilters.niche.join(",");

        // Age is only sent once the user sets a range; the top of the slider
        // means "and older", so no upper bound is sent for it
        if (currentFilters.age.range) {
          params.minAge = currentFilters.age.range[0];
          if (currentFilters.age.range[1] < AGE_MAX)
            params.maxAge = currentFilters.age.range[1];
        }

        if (currentFilters.revenue.min != null)
          params.minRevenue = currentFilters.revenue.min;
        if (currentFilters.revenue.max != null)
          params.maxRevenue = currentFilters.revenue.max;

        const response = await programService.getApplications(params);

        setApplications(response.data || []);
        setTotal(response.pagination.total || 0);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Failed to load applications");
        setApplications([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  // Debounced search handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(1);
      fetchApplications(value, filters);
    }, 500),
    [fetchApplications, filters]
  );

  // Handle search input
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Filters apply live, same as the Active affiliates tab
  const updateFilters = (patch: Partial<FilterState>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setCurrentPage(1);
    fetchApplications(searchQuery, next);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery("");
    setCurrentPage(1);
    fetchApplications("", DEFAULT_FILTERS);
  };

  // Initial fetch and page change handler
  useEffect(() => {
    fetchApplications(searchQuery, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchApplications, currentPage]);

  const pendingApplications = (applications || []).filter(
    (app) => app.status === "PENDING"
  );

  // Selection is keyed on applicationId, not the affiliate id: an affiliate can
  // apply to several of the brand's programs, and each of those is approved or
  // rejected on its own.
  const handleSelectAll = () => {
    if (selectedApplicants.length === pendingApplications.length) {
      setSelectedApplicants([]);
    } else {
      setSelectedApplicants(pendingApplications.map((app) => app.applicationId));
    }
  };

  const handleSelectApplicant = (
    id: string,
    status: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (status !== "PENDING") return;

    setSelectedApplicants((prev) =>
      prev.includes(id) ? prev.filter((appId) => appId !== id) : [...prev, id]
    );
  };

  const handleRowClick = (applicant: ProgramApplication) => {
    navigate(`/brand/affiliates/${applicant.id}`);
  };

  const handleApproveSelected = async () => {
    try {
      setIsProcessing((prev) => ({ ...prev, approve: true }));
      const loadingToast = toast.loading("Approving selected applications...");

      await programService.approveApplications(selectedApplicants);
      toast.dismiss(loadingToast);
      toast.success("Successfully approved selected applications");

      // Refresh the applications list
      const data = await programService.getApplications();
      setApplications(data.data);

      // Clear selection
      setSelectedApplicants([]);
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 400 &&
        error.response?.data?.message
      ) {
        const message = error.response.data.message;
        if (Array.isArray(message)) {
          message.forEach((msg: string) => {
            toast.error(msg);
          });
        } else {
          toast.error(message);
        }
      } else {
        console.error("Error approving applications:", error);
        toast.error("Failed to approve applications");
      }
    } finally {
      setIsProcessing((prev) => ({ ...prev, approve: false }));
    }
  };

  const handleRejectSelected = async () => {
    try {
      setIsProcessing((prev) => ({ ...prev, reject: true }));
      const loadingToast = toast.loading("Rejecting selected applications...");

      await programService.rejectApplications(selectedApplicants);
      toast.dismiss(loadingToast);
      toast.success("Successfully rejected selected applications");

      // Refresh the applications list
      const data = await programService.getApplications();
      setApplications(data.data);

      // Clear selection
      setSelectedApplicants([]);
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 400 &&
        error.response?.data?.message
      ) {
        const message = error.response.data.message;
        if (Array.isArray(message)) {
          message.forEach((msg: string) => {
            toast.error(msg);
          });
        } else {
          toast.error(message);
        }
      } else {
        console.error("Error rejecting applications:", error);
        toast.error("Failed to reject applications");
      }
    } finally {
      setIsProcessing((prev) => ({ ...prev, reject: false }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<ProgramApplication, any>[] = [
    {
      id: "select",
      header: () => null,
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedApplicants.includes(row.original.applicationId)}
          onChange={() => {}}
          onClick={(e) =>
            handleSelectApplicant(
              row.original.applicationId,
              row.original.status,
              e
            )
          }
          className={`rounded border-border text-brand-600 focus:ring-brand-500 ${
            row.original.status !== "PENDING"
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
          }`}
          disabled={row.original.status !== "PENDING"}
        />
      ),
      meta: {
        width: "w-10",
        skeleton: <div className="h-4 w-4 rounded bg-muted animate-pulse" />,
      },
    },
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-3 min-w-0">
          {row.original.avatar ? (
            <img
              src={row.original.avatar}
              alt={`${row.original.firstName} ${row.original.lastName}`}
              className="w-8 h-8 rounded-full shrink-0 object-cover"
            />
          ) : (
            <span
              aria-hidden
              className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center bg-brand-100 text-brand-700 text-xs font-semibold"
            >
              {`${row.original.firstName?.[0] ?? ""}${row.original.lastName?.[0] ?? ""}`.toUpperCase() || "?"}
            </span>
          )}
          <span className="break-words font-medium">{`${row.original.firstName} ${row.original.lastName}`}</span>
        </div>
      ),
      meta: {
        width: "w-[16%]",
        skeleton: (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
          </div>
        ),
      },
    },
    {
      id: "username",
      header: "Username",
      cell: ({ row }) =>
        row.original.username ? (
          <span className="block break-all text-muted-foreground">
            @{row.original.username}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      meta: { width: "w-[12%]" },
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <div className="flex items-center">
          <span
            className={`inline-flex items-center gap-1 ${
              row.original.status === "PENDING"
                ? "text-yellow-700"
                : row.original.status === "APPROVED" ||
                  row.original.status === "ACTIVE"
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                row.original.status === "PENDING"
                  ? "bg-yellow-500"
                  : row.original.status === "APPROVED" ||
                    row.original.status === "ACTIVE"
                  ? "bg-green-500"
                  : "bg-red-500"
              }`}
            ></span>
            <span>
              {row.original.status === "ACTIVE"
                ? "APPROVED"
                : row.original.status}
            </span>
          </span>
        </div>
      ),
      meta: { width: "w-28" },
    },
    {
      id: "email",
      header: "Email address",
      cell: ({ row }) => (
        <span className="block break-all">{row.original.email}</span>
      ),
      meta: { width: "w-[16%]" },
    },
    {
      id: "program",
      header: "Program",
      cell: ({ row }) => (
        <span className="block break-all text-sm text-muted-foreground">
          {row.original.programName}
        </span>
      ),
      meta: { width: "w-[12%]" },
    },
    {
      id: "code",
      header: "Code",
      cell: ({ row }) =>
        row.original.code ? (
          <span className="font-medium text-foreground">
            {row.original.code}
          </span>
        ) : (
          // Sneakylink programs mint a fresh code on every click, so there is
          // nothing to show until the affiliate is approved and gets a link.
          <span className="text-sm text-muted-foreground">
            {row.original.attributionMethod === "SNEAKYLINK"
              ? "Sneakylink"
              : "—"}
          </span>
        ),
      meta: { width: "w-24" },
    },
    {
      id: "social",
      header: "Social Media",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.original.socialMedias &&
          row.original.socialMedias.length > 0 ? (
            row.original.socialMedias.map((social, index) => {
              const SocialIcon = () => {
                switch (social.socialMedia.toLowerCase()) {
                  case "instagram":
                    return <Instagram className="h-4 w-4 text-pink-600" />;
                  case "tiktok":
                    return <TikTokIcon className="h-4 w-4 text-foreground" />;
                  case "youtube":
                    return <Youtube className="h-4 w-4 text-red-600" />;
                  case "facebook":
                    return <Facebook className="h-4 w-4 text-blue-600" />;
                  default:
                    return null;
                }
              };

              return (
                <a
                  key={index}
                  href={`https://${social.socialMedia.toLowerCase()}.com/${
                    social.username
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand-600 transition-colors"
                >
                  <SocialIcon />
                  {/* <span>@{social.username}</span> */}
                </a>
              );
            })
          ) : (
            <span className="text-muted-foreground">
              No social media linked
            </span>
          )}
        </div>
      ),
      meta: { width: "w-32" },
    },
    {
      id: "appliedDate",
      header: "Applied Date",
      cell: ({ row }) => (
        <span>
          {new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).format(new Date(row.original.createdAt))}
        </span>
      ),
      meta: { width: "w-28" },
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Affiliate applications" showDefaultActions={true} />

      {/* Filters */}
      <div className="mb-6 bg-card p-4 sm:p-6 rounded-xl border border-border shadow-sm">
        <AffiliateFilterBar
          searchValue={searchQuery}
          onSearchChange={handleSearch}
          searchPlaceholder="Search name or email"
          nicheOptions={niches.map((niche) => ({
            label: niche.name,
            value: niche.id,
          }))}
          niche={filters.niche}
          onNicheChange={(value) => updateFilters({ niche: value })}
          gender={filters.gender}
          onGenderChange={(value) => updateFilters({ gender: value })}
          country={filters.country}
          onCountryChange={(value) => updateFilters({ country: value })}
          age={filters.age}
          onAgeChange={(value) => updateFilters({ age: value })}
          revenue={filters.revenue}
          onRevenueChange={(value) => updateFilters({ revenue: value })}
          onResetAll={handleResetFilters}
        />
      </div>

      {/* Table */}
      <TableCard>
        {error ? (
          <div className="text-center py-8 text-sm text-red-600">{error}</div>
        ) : !isLoading && applications.length === 0 ? (
          <EmptyState
            bordered={false}
            icon={<Users />}
            title="No applications yet"
            body="When affiliates apply to join your program, they'll appear here"
          />
        ) : (
          <>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={
                    selectedApplicants.length === pendingApplications.length &&
                    pendingApplications.length > 0
                  }
                  onChange={handleSelectAll}
                  className={`rounded border-border text-brand-600 focus:ring-brand-500 ${
                    pendingApplications.length === 0
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  disabled={pendingApplications.length === 0}
                />
                <span className="text-sm text-muted-foreground">
                  {selectedApplicants.length === 0
                    ? `Select all (${pendingApplications.length} pending)`
                    : selectedApplicants.length === pendingApplications.length
                    ? "Unselect all"
                    : `Selected ${selectedApplicants.length} of ${pendingApplications.length} pending`}
                </span>
              </div>
              {selectedApplicants.length > 0 && (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleRejectSelected}
                    disabled={isProcessing.reject || isProcessing.approve}
                    className={`text-sm text-red-600 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isProcessing.reject
                      ? "Rejecting..."
                      : `Reject selected (${selectedApplicants.length})`}
                  </button>
                  <button
                    onClick={handleApproveSelected}
                    disabled={isProcessing.approve || isProcessing.reject}
                    className={`text-sm text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isProcessing.approve
                      ? "Approving..."
                      : `Approve selected (${selectedApplicants.length})`}
                  </button>
                </div>
              )}
            </div>
            <DataTable
              columns={columns}
              data={applications}
              isLoading={isLoading}
              emptyText="No applications found"
              getRowId={(row) => row.applicationId}
              onRowClick={handleRowClick}
            />
            <DataTablePagination
              page={currentPage}
              totalPages={Math.max(Math.ceil(total / pageSize), 1)}
              total={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              noun="applicants"
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
